/**
 * CanvasHiResImageViewer — shared hi-res image inspection surface.
 *
 * Quality contract:
 * - Opens on DISPLAY_PREVIEW (`displaySrc`) immediately
 * - Lazily upgrades to GENERATED_MASTER / SOURCE_ORIGINAL (`masterSrc`)
 * - 100% = 1 image pixel : 1 CSS pixel
 * - Zoom/pan is local only — never writes the Canvas graph
 *
 * VistoeraTASK-00575 — also the workbench Image Assets viewer surface.
 * Optional chrome (`bottomPanel` / `showZoomToolbar` / hide flags) is
 * additive and off by default so Canvas MediaLightbox behavior stays frozen.
 */

import { CloseOutlined, ExpandOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../../../utils/scrollLock'
import { resolveCanvasHiResViewerPortalRoot } from './canvasHiResViewerPortal'
import {
  actualSizeTransform,
  clampPan,
  computeFitScale,
  fitTransform,
  formatZoomPercent,
  isNearActual,
  isNearFit,
  panBy,
  remapTransformForImageSize,
  zoomAtPoint,
  type HiResTransform,
  type Size,
} from './canvasHiResImageTransform'
import { getCachedMaster, loadMasterImage } from './canvasHiResMasterCache'
import {
  formatMasterMetaLabel,
  type CanvasHiResMasterMeta,
} from './canvasHiResMasterMeta'

export type { CanvasHiResMasterMeta }

type HiresPhase = 'idle' | 'loading' | 'ready' | 'failed'

const ZOOM_STEP = 1.2

export interface CanvasHiResImageViewerProps {
  open: boolean
  onClose: () => void
  /** DISPLAY_PREVIEW — shown immediately. */
  displaySrc: string | null
  /** GENERATED_MASTER / SOURCE_ORIGINAL — progressive upgrade. */
  masterSrc?: string | null
  /** Master-only metadata (never preview/web/thumbnail bytes). */
  masterMeta?: CanvasHiResMasterMeta | null
  /** Optional legacy chip override; preferred path is structured masterMeta. */
  masterMetaLabel?: string | null
  /** Preserve existing bottom-right business actions (识图提问, etc.). */
  footerActions?: ReactNode
  /**
   * In-flow panel below the viewport. Deducts from Fit viewport height.
   * Canvas default: omitted (meta/actions stay overlaid absolute chrome).
   */
  bottomPanel?: ReactNode
  /** Hide the absolute top-right close control (e.g. workbench footer Close). */
  hideDefaultClose?: boolean
  /** Hide the floated master-meta chip. */
  hideMetaChip?: boolean
  /** Optional +/- / Fit toolbar at the bottom-center of the viewport. */
  showZoomToolbar?: boolean
  /**
   * Optional chrome inside the viewport (e.g. workbench prev/next).
   * Rendered above the image, sibling to the zoom toolbar — must not
   * change Fit / zoom / pan math.
   */
  viewportOverlay?: ReactNode
  /** Extra class on the viewport (e.g. transparent checkerboard). */
  viewportClassName?: string
  /** Extra class on the outer viewer root. */
  className?: string
}

const EMPTY_TRANSFORM: HiResTransform = { scale: 1, tx: 0, ty: 0 }

export default function CanvasHiResImageViewer({
  open,
  onClose,
  displaySrc,
  masterSrc = null,
  masterMeta = null,
  masterMetaLabel = null,
  footerActions = null,
  bottomPanel = null,
  hideDefaultClose = false,
  hideMetaChip = false,
  showZoomToolbar = false,
  viewportOverlay = null,
  viewportClassName,
  className,
}: CanvasHiResImageViewerProps) {
  const { t, i18n } = useTranslation('canvas')
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const transformRef = useRef<HiResTransform>(EMPTY_TRANSFORM)
  const imageSizeRef = useRef<Size>({ w: 0, h: 0 })
  const viewportSizeRef = useRef<Size>({ w: 0, h: 0 })
  const dragRef = useRef<{ pointerId: number; x: number; y: number; tx: number; ty: number } | null>(null)
  const identityKeyRef = useRef<string>('')
  const [openEpoch, setOpenEpoch] = useState(0)

  useModalScrollLock(open)

  const [displayUrl, setDisplayUrl] = useState<string | null>(displaySrc)
  const [hiresPhase, setHiresPhase] = useState<HiresPhase>('idle')
  const [showReadyBadge, setShowReadyBadge] = useState(false)
  const [transform, setTransform] = useState<HiResTransform>(EMPTY_TRANSFORM)
  const [imageSize, setImageSize] = useState<Size>({ w: 0, h: 0 })
  const [canPan, setCanPan] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [zoomHint, setZoomHint] = useState<string | null>(null)
  const [naturalFromMaster, setNaturalFromMaster] = useState<Size>({ w: 0, h: 0 })

  const applyTransform = useCallback((next: HiResTransform) => {
    transformRef.current = next
    setTransform(next)
    const vw = viewportSizeRef.current
    const img = imageSizeRef.current
    const overflow = img.w * next.scale > vw.w + 0.5 || img.h * next.scale > vw.h + 0.5
    setCanPan(overflow)
    if (imgRef.current) {
      imgRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) scale(${next.scale})`
    }
  }, [])

  const measureViewport = useCallback((): Size => {
    const el = viewportRef.current
    if (!el) return viewportSizeRef.current
    const rect = el.getBoundingClientRect()
    const size = { w: rect.width, h: rect.height }
    viewportSizeRef.current = size
    return size
  }, [])

  const resetToFit = useCallback(
    (size: Size) => {
      const viewport = measureViewport()
      if (size.w <= 0 || size.h <= 0 || viewport.w <= 0 || viewport.h <= 0) return
      imageSizeRef.current = size
      setImageSize(size)
      applyTransform(fitTransform(size, viewport))
    },
    [applyTransform, measureViewport],
  )

  // Always reset fit/pan when the viewer opens (incl. close→reopen same asset).
  // Also reset when the inspected image identity changes (A → B).
  useEffect(() => {
    if (!open) {
      identityKeyRef.current = ''
      return
    }
    const key = `${(displaySrc || '').trim()}|${(masterSrc || '').trim()}`
    identityKeyRef.current = key
    setOpenEpoch((n) => n + 1)
    setDisplayUrl(displaySrc)
    setHiresPhase('idle')
    setShowReadyBadge(false)
    setNaturalFromMaster({ w: 0, h: 0 })
    imageSizeRef.current = { w: 0, h: 0 }
    setImageSize({ w: 0, h: 0 })
    applyTransform(EMPTY_TRANSFORM)
  }, [open, displaySrc, masterSrc, applyTransform])

  // Progressive master load (+ session cache).
  useEffect(() => {
    if (!open) return
    const preview = (displaySrc || '').trim()
    const master = (masterSrc || '').trim()
    setDisplayUrl(preview || null)

    if (!master) {
      setHiresPhase('idle')
      return
    }
    if (master === preview) {
      const cached = getCachedMaster(master)
      if (cached) {
        setNaturalFromMaster({ w: cached.width, h: cached.height })
        setHiresPhase('ready')
      } else {
        setHiresPhase('ready')
      }
      setDisplayUrl(master)
      return
    }

    const cached = getCachedMaster(master)
    if (cached) {
      setNaturalFromMaster({ w: cached.width, h: cached.height })
      setDisplayUrl(master)
      setHiresPhase('ready')
      return
    }

    let cancelled = false
    setHiresPhase('loading')
    loadMasterImage(master)
      .then((entry) => {
        if (cancelled) return
        setNaturalFromMaster({ w: entry.width, h: entry.height })
        setDisplayUrl(master)
        setHiresPhase('ready')
        setShowReadyBadge(true)
      })
      .catch(() => {
        if (cancelled) return
        setHiresPhase('failed')
      })
    return () => {
      cancelled = true
    }
  }, [open, displaySrc, masterSrc])

  useEffect(() => {
    if (!showReadyBadge) return
    const timer = window.setTimeout(() => setShowReadyBadge(false), 1600)
    return () => window.clearTimeout(timer)
  }, [showReadyBadge])

  useEffect(() => {
    if (!zoomHint) return
    const timer = window.setTimeout(() => setZoomHint(null), 1200)
    return () => window.clearTimeout(timer)
  }, [zoomHint])

  // Escape close (IME-safe: skip when typing in editable fields).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return
      }
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  // Wheel zoom — non-passive, viewer-scoped only. Depend on viewportEl so the
  // listener attaches after the portal mounts (ref-only effects can miss).
  useEffect(() => {
    if (!open || !viewportEl) return
    const el = viewportEl
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const img = imageSizeRef.current
      const viewport = measureViewport()
      if (img.w <= 0 || img.h <= 0 || viewport.w <= 0 || viewport.h <= 0) return
      const rect = el.getBoundingClientRect()
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      const prev = transformRef.current
      // ctrlKey covers trackpad pinch-to-zoom on most browsers.
      const intensity = e.ctrlKey ? 0.012 : 0.0025
      const factor = Math.exp(-e.deltaY * intensity)
      const nextScale = prev.scale * factor
      const next = zoomAtPoint(prev, nextScale, px, py, img, viewport)
      applyTransform(next)
      setZoomHint(formatZoomPercent(next.scale))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [open, viewportEl, applyTransform, measureViewport])

  // Keep pan/fit correct on resize.
  useLayoutEffect(() => {
    if (!open) return
    const el = viewportRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const viewport = measureViewport()
      const img = imageSizeRef.current
      if (img.w <= 0 || img.h <= 0) return
      applyTransform(clampPan(transformRef.current, img, viewport))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, applyTransform, measureViewport])

  const onImageLoad = (el: HTMLImageElement) => {
    const nextSize = { w: el.naturalWidth, h: el.naturalHeight }
    if (nextSize.w <= 0 || nextSize.h <= 0) return
    const prevSize = imageSizeRef.current
    const viewport = measureViewport()
    if (prevSize.w <= 0 || prevSize.h <= 0) {
      resetToFit(nextSize)
      return
    }
    // Master swap after preview: remap without visual jump.
    if (prevSize.w !== nextSize.w || prevSize.h !== nextSize.h) {
      imageSizeRef.current = nextSize
      setImageSize(nextSize)
      applyTransform(remapTransformForImageSize(transformRef.current, prevSize, nextSize, viewport))
      return
    }
    imageSizeRef.current = nextSize
    setImageSize(nextSize)
    applyTransform(clampPan(transformRef.current, nextSize, viewport))
  }

  const onDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const img = imageSizeRef.current
    const viewport = measureViewport()
    if (img.w <= 0 || img.h <= 0) return
    const rect = viewportRef.current?.getBoundingClientRect()
    if (!rect) return
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const prev = transformRef.current
    const fit = computeFitScale(img, viewport)
    const next = isNearFit(prev.scale, fit) || prev.scale < fit * 1.05
      ? actualSizeTransform(img, viewport, px, py, prev)
      : fitTransform(img, viewport)
    applyTransform(next)
    setZoomHint(formatZoomPercent(next.scale))
  }

  const zoomByFactor = (factor: number) => {
    const img = imageSizeRef.current
    const viewport = measureViewport()
    if (img.w <= 0 || img.h <= 0 || viewport.w <= 0 || viewport.h <= 0) return
    const prev = transformRef.current
    const next = zoomAtPoint(prev, prev.scale * factor, viewport.w / 2, viewport.h / 2, img, viewport)
    applyTransform(next)
    setZoomHint(formatZoomPercent(next.scale))
  }

  const fitToWindow = () => {
    const img = imageSizeRef.current
    const viewport = measureViewport()
    if (img.w <= 0 || img.h <= 0 || viewport.w <= 0 || viewport.h <= 0) return
    const next = fitTransform(img, viewport)
    applyTransform(next)
    setZoomHint(formatZoomPercent(next.scale))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    const img = imageSizeRef.current
    const viewport = viewportSizeRef.current
    const overflow = img.w * transformRef.current.scale > viewport.w + 0.5 || img.h * transformRef.current.scale > viewport.h + 0.5
    if (!overflow) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      tx: transformRef.current.tx,
      ty: transformRef.current.ty,
    }
    setDragging(true)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dx = e.clientX - drag.x
    const dy = e.clientY - drag.y
    const next = panBy(
      { scale: transformRef.current.scale, tx: drag.tx, ty: drag.ty },
      dx,
      dy,
      imageSizeRef.current,
      viewportSizeRef.current,
    )
    // High-frequency path: DOM first, React state coalesced.
    transformRef.current = next
    if (imgRef.current) {
      imgRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) scale(${next.scale})`
    }
  }

  const endDrag = (e: React.PointerEvent) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragRef.current = null
    setDragging(false)
    applyTransform(transformRef.current)
  }

  const statusLabel =
    hiresPhase === 'loading'
      ? t('mediaLightbox.loadingHires')
      : hiresPhase === 'failed'
        ? t('mediaLightbox.hiresFailed')
        : hiresPhase === 'ready' && showReadyBadge
          ? t('mediaLightbox.hiresReady')
          : null

  const resolvedMetaLabel =
    masterMetaLabel ||
    formatMasterMetaLabel(masterMeta, {
      locale: i18n.language,
      generatedLabel: t('mediaLightbox.generated'),
      uploadedLabel: t('mediaLightbox.uploaded'),
    }) ||
    (naturalFromMaster.w > 0 && naturalFromMaster.h > 0
      ? `${naturalFromMaster.w} × ${naturalFromMaster.h} px`
      : imageSize.w > 0 && imageSize.h > 0 && hiresPhase === 'ready'
        ? `${imageSize.w} × ${imageSize.h} px`
        : null)

  // Prefer structured master dimensions over preview natural size when ready.
  const footerMeta = resolvedMetaLabel

  const portalRoot = useMemo(() => (open ? resolveCanvasHiResViewerPortalRoot() : null), [open])

  if (!open || !displayUrl || !portalRoot) return null

  const cursor = dragging ? 'grabbing' : canPan ? 'grab' : 'default'

  const rootClass = ['pcv-hires-viewer', MODAL_SURFACE_CLASS, className].filter(Boolean).join(' ')
  const viewportClass = ['pcv-hires-viewer-viewport', viewportClassName].filter(Boolean).join(' ')

  return createPortal(
    <div
      className={rootClass}
      data-testid="canvas-hires-image-viewer"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="pcv-hires-viewer-stage"
        data-testid="hires-viewer-stage"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!hideDefaultClose ? (
          <button
            type="button"
            className="pcv-hires-viewer-close"
            aria-label={t('actions.close')}
            data-testid="hires-viewer-close"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        ) : null}

        {(statusLabel || zoomHint) && (
          <div className="pcv-hires-viewer-status" data-testid="lightbox-hires-status">
            {statusLabel ? (
              <span className={`pcv-hires-viewer-status-text is-${hiresPhase}`}>{statusLabel}</span>
            ) : null}
            {zoomHint ? (
              <span className="pcv-hires-viewer-zoom-hint" data-testid="hires-zoom-hint">
                {zoomHint}
              </span>
            ) : null}
          </div>
        )}

        <div
          ref={(el) => {
            viewportRef.current = el
            setViewportEl(el)
          }}
          className={viewportClass}
          data-testid="hires-viewer-viewport"
          style={{ cursor }}
          onDoubleClick={onDoubleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <img
            key={`hires-${openEpoch}-${displayUrl}`}
            ref={imgRef}
            className="pcv-hires-viewer-image"
            src={displayUrl}
            alt=""
            draggable={false}
            data-testid="lightbox-image"
            data-hires={hiresPhase === 'ready' && displayUrl === (masterSrc || '').trim() ? 'yes' : 'no'}
            data-scale={transform.scale}
            data-near-fit={isNearFit(transform.scale, computeFitScale(imageSize, viewportSizeRef.current)) ? 'yes' : 'no'}
            data-near-actual={isNearActual(transform.scale) ? 'yes' : 'no'}
            style={{
              width: imageSize.w > 0 ? imageSize.w : undefined,
              height: imageSize.h > 0 ? imageSize.h : undefined,
              transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
              transformOrigin: '0 0',
            }}
            onLoad={(e) => onImageLoad(e.currentTarget)}
          />

          {viewportOverlay ? (
            <div
              className="pcv-hires-viewer-viewport-overlay"
              data-testid="hires-viewer-viewport-overlay"
              onPointerDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              {viewportOverlay}
            </div>
          ) : null}

          {showZoomToolbar ? (
            <div
              className="pcv-hires-viewer-zoombar"
              data-testid="hires-viewer-zoombar"
              onPointerDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="pcv-hires-viewer-zoombar-btn"
                aria-label="Zoom out"
                onClick={() => zoomByFactor(1 / ZOOM_STEP)}
              >
                <MinusOutlined />
              </button>
              <span className="pcv-hires-viewer-zoom-label" data-testid="hires-zoom-label">
                {formatZoomPercent(transform.scale)}
              </span>
              <button
                type="button"
                className="pcv-hires-viewer-zoombar-btn"
                aria-label="Zoom in"
                onClick={() => zoomByFactor(ZOOM_STEP)}
              >
                <PlusOutlined />
              </button>
              <button
                type="button"
                className="pcv-hires-viewer-zoombar-btn"
                aria-label="Fit to window"
                data-testid="hires-fit-to-window"
                onClick={fitToWindow}
              >
                <ExpandOutlined />
              </button>
            </div>
          ) : null}
        </div>

        {!hideMetaChip && footerMeta ? (
          <div className="pcv-hires-viewer-meta" data-testid="hires-viewer-meta">
            {footerMeta}
          </div>
        ) : null}

        {footerActions ? <div className="pcv-hires-viewer-actions">{footerActions}</div> : null}

        {bottomPanel ? (
          <div className="pcv-hires-viewer-bottom-panel" data-testid="hires-viewer-bottom-panel">
            {bottomPanel}
          </div>
        ) : null}
      </div>
    </div>,
    portalRoot,
  )
}
